import React, { ChangeEvent, useState, useEffect } from "react";
import {
  AppBar,
  Box,
  Button,
  IconButton,
  MenuList,
  MenuItem,
  Paper,
  Popover,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import styles from "./LPSForm.styles";
import LPSGeneralFieldsBlock from "./components/LPSGeneralFieldsBlock";
import ObjectPointsBlock from "./components/ObjectPointsBlock";
import ObjectTypesBlock from "./components/ObjectTypesBlock";
import LPSEntityKOABlock from "./components/LPSEntityKOABlock";
import {
  lpsFormTypes,
  userTypes,
} from '../../common/constants/field-constants';
import { Clear, Info, ContentCopy } from "@mui/icons-material";
import { connect, useDispatch } from "react-redux";
import DeleteIcon from "@mui/icons-material/Delete";
import { validateLPSConfig, Variable } from "../../common/components/helpers/validator";
import {
  createEditLPSConfigsRequest,
  deleteLPSConfigRequest,
  receiveLPSConfigsRequest,
} from "./actions";
import { selectUser } from "../login/selectors";
import { selectLPS } from "./selectors";
import AlertService  from "../../common/services/notification.service";
import type { RootState } from '../../reducers';
import { SelectChangeEvent } from '@mui/material/Select';

const LPS_CONFIG_ENTITY_KOA_RULES = "lps_config_entity_KOA_rules";

interface LPSConfigEntityKOARulesProps {
  id?: number;
  lps_config_entity_KOA_points: string
}

interface LPSFormProps {
  user?: any;
  lpsForm?: any;
}

const lpsFormInitialState = {
  id: null,
  user_id: null,
  lps_config_cities: [],
  general_fields: {
    name: "",
    calculation_type: lpsFormTypes.NON_INDUSTRIAL,
    config_type: "",
    object_type: "",
    entity_type: "",
    // city: "", //temp till search impl.
  },
  lpsc_object_condition_rules: [
    {
      variable: "units amount",
      condition: "",
      condition_value: "",
      condition_points: "",
    },
  ],
  lpsc_object_types_rules: [
    {
      variable: "",
      condition: "",
      unit_size: "",
    },
  ],
  lps_config_entity_KOA_rules: {
    lps_config_entity_KOA_points: "",
  } as LPSConfigEntityKOARulesProps,
  action_types: [] as string[],
  cityForDelete: [],
  validationMode: false,
  objectRulesForDelete: [],
  objectTypeRulesForDelete: [],
  // base_points: 0,
  // config_type: "",
  // lps_config_entity_rules: {},
  // lpsc_entity_objects_rules: [],
  // lpsc_entity_age_rules: [],
  // lpsc_object_label_rules: [],
  // labelForDelete: [],
  // entityAgeForDelete: [],
  // entityObjForDelete: [],
};

export const MAX_NAME_LENGTH = 20;

const LPSForm = ({ user, lpsForm }: LPSFormProps) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const [clearForm, setClearForm] = useState<boolean>(false);
  const [configCities, setConfigCities] = useState([]);
  const [lpsFieldsForSave, setLpsFieldsForSave] = useState(lpsFormInitialState);
  const [filledFields, setFilledFields] = useState<object>({});
  const [variables, setVariables] = useState<Variable[]>([]);
  const [error, setError] = useState("");
  const dispatch = useDispatch();
  const open: boolean = Boolean(anchorEl);
  const id: "simple-popover" | undefined = open ? "simple-popover" : undefined;
  const { userId, userRole } = user;
  const { lpsConfigs } = lpsForm;

  useEffect(() => {
    if (!!error) {
      AlertService.warning(error)
    }
  }, [error]);

  const handleClose = () => {
    setAnchorEl(null);
  };
  const validateLength = (value: string) => {
    if (value.length > MAX_NAME_LENGTH) {
      setError(`Maximum ${MAX_NAME_LENGTH} characters`);
      return false;
    } else {
      setError(``);
      return true;
    }
  }

  const selectConfig = (config: any): void => {
    setConfigCities(config.lps_config_cities);
    setLpsFieldsForSave({
      id: config.id,
      user_id: config.user_id,
      lps_config_cities: config.lps_config_cities,
      general_fields: {
        name: config.name,
        calculation_type: config.calculation_type,
        config_type: config.config_type,
        object_type: config.object_type,
        entity_type: config.entity_type,
      },
      lpsc_object_condition_rules: config.lpsc_object_condition_rules
        ? config.lpsc_object_condition_rules
        : [
            {
              variable: "",
              condition: "",
              condition_value: "",
              condition_points: "",
            },
          ],
      lpsc_object_types_rules: config.lpsc_object_types_rules
        ? config.lpsc_object_types_rules
        : [
            {
              variable: "",
              condition: "",
              unit_size: "",
            },
          ],
      lps_config_entity_KOA_rules: config.lps_config_entity_KOA_rules
        ? config.lps_config_entity_KOA_rules
        : {
            lps_config_entity_KOA_points: "",
          },
      action_types: [],
      cityForDelete: [],
      validationMode: false,
      objectRulesForDelete: [],
      objectTypeRulesForDelete: [],
    });
    handleClose();
  };

  const handleChangeCity = (newCity: any): void => {
    const currentType = "cities";
    setClearForm(false);
    const result = newCity.map((item: any, _index: number) => {
      const base = {
        city_id: item.cityId,
        city_name: item.nameCity,
      }
      if (item.configCityId) {
        return {...base, id: item.configCityId};
      }
      return base;
    });
    setLpsFieldsForSave({
      ...lpsFieldsForSave,
      // @ts-ignore
      lps_config_cities: [...result],
      // @ts-ignore
      action_types: lpsFieldsForSave.action_types.includes(currentType)
        ? lpsFieldsForSave.action_types
        : [currentType, ...lpsFieldsForSave.action_types],
    });
  };
  const handlerBlurName = () => {
    if (!validateLength(lpsFieldsForSave.general_fields.name)){
      setLpsFieldsForSave({...lpsFieldsForSave,
        general_fields: {
          ...lpsFieldsForSave.general_fields,
          name: lpsFieldsForSave.general_fields.name.slice(0, MAX_NAME_LENGTH),
        },
      })
      setError("");
    }
  }

  const handleChange = (
    fieldName: string,
    key: string,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent,
  ) => {
    const currentType = "general_fields"; //for config update
    let value = event.target.value;

    if (key === "name") {
       value = value.toUpperCase();
       validateLength(value);
    }

    if (fieldName === "generalFields") {
      if (lpsFieldsForSave.general_fields.calculation_type === lpsFormTypes.KADASTER) {
        setLpsFieldsForSave({
          ...lpsFieldsForSave,
          lpsc_object_condition_rules: [
            {
              variable: "",
              condition: "",
              condition_value: "",
              condition_points: "",
            },
          ],
          lpsc_object_types_rules: [
            {
              variable: "",
              condition: "",
              unit_size: "",
            },
          ],
          general_fields: {
            ...lpsFieldsForSave.general_fields,
            [key]: value,
          },
          lps_config_cities: [],
          // @ts-ignore
          action_types: lpsFieldsForSave.action_types.includes(currentType)
            ? lpsFieldsForSave.action_types
            : [currentType, ...lpsFieldsForSave.action_types],
        })
      } else {
        setLpsFieldsForSave({
          ...lpsFieldsForSave,
          lps_config_entity_KOA_rules: {
            lps_config_entity_KOA_points: "",
          },
          general_fields: {
            ...lpsFieldsForSave.general_fields,
            [key]: value,
          },
          // @ts-ignore
          action_types: lpsFieldsForSave.action_types.includes(currentType)
            ? lpsFieldsForSave.action_types
            : [currentType, ...lpsFieldsForSave.action_types],
        });
      }
    }
  };

  const handleChangeObjectConditionRules = (
    blockName: string,
    fieldName: string,
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent,
    index: number,
  ) => {
    let currentType: string
    if (blockName === "object_points_condition_rules") {
      currentType = "lpsc_object_condition_rules";
    }
    const editableBlock = lpsFieldsForSave.lpsc_object_condition_rules;
    let textFieldValue: string = e.target.value;

    if (fieldName === 'condition_value' || fieldName === 'condition_points' ) {
      if (/^\d*\.?\d*$/.test(textFieldValue)) {
        const roundedFieldValue: number = Math.round(Number(textFieldValue));
        editableBlock[index] = {
          ...editableBlock[index],
          [fieldName]: roundedFieldValue,
        }
      } else {
        e.preventDefault();
      }
    } else {
      editableBlock[index] = {
        ...editableBlock[index],
        [fieldName]: textFieldValue,
      };
    }

    return setLpsFieldsForSave( prev => {
      const updatedActionTypes = Array.from(new Set([
          ...(prev.action_types ?? []),
          currentType,
          LPS_CONFIG_ENTITY_KOA_RULES,
        ])
      )
      return {
        ...prev,
        lps_config_entity_KOA_rules: {
             id: lpsFieldsForSave?.lps_config_entity_KOA_rules?.id,
             lps_config_entity_KOA_points: "",
        },
        [currentType]: [...editableBlock],
        action_types: updatedActionTypes,
      }
    })
  };

  const handleChangeObjectTypesRules = (
    blockName: string,
    fieldName: string,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent,
    index: number,
  ) => {
    const currentType = "lpsc_object_types_rules";
    const value: string = event.target.value;
    const editableBlock = lpsFieldsForSave.lpsc_object_types_rules;
    editableBlock[index] = {
      ...editableBlock[index],
      [fieldName]: value,
    };
    return setLpsFieldsForSave(prev => {
      const updatedActionTypes = Array.from(new Set([
          ...(prev.action_types ?? []),
          currentType,
          LPS_CONFIG_ENTITY_KOA_RULES,
        ])
      )
      return {
        ...prev,
        lps_config_entity_KOA_rules: {
          id: lpsFieldsForSave?.lps_config_entity_KOA_rules?.id,
          lps_config_entity_KOA_points: "",
        },
        [blockName]: [...editableBlock],
        action_types: updatedActionTypes,
      }
    })
    // return setLpsFieldsForSave({
    //   ...lpsFieldsForSave,
    //   lps_config_entity_KOA_rules: {
    //     // @ts-ignore
    //     id: lpsFieldsForSave.lps_config_entity_KOA_rules.id,
    //     lps_config_entity_KOA_points: "",
    //   },
    //   [blockName]: [...editableBlock],
    //   // @ts-ignore
    //   action_types: lpsFieldsForSave.action_types.includes(currentType)
    //     ? [...lpsFieldsForSave.action_types, "lps_config_entity_KOA_rules"]
    //     : [
    //         currentType,
    //         "lps_config_entity_KOA_rules",
    //         ...lpsFieldsForSave.action_types,
    //       ],
    // });
  };

  const handleChangeEntityKOA = (
    blockName: string,
    fieldName: string,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const currentType = "lps_config_entity_KOA_rules";
    const value = event.target.value;
    const numberValue = Number(value);
    let editableBlock: {lps_config_entity_KOA_points: string | number}  = lpsFieldsForSave.lps_config_entity_KOA_rules;

    if (fieldName === "lps_config_entity_KOA_points") {
      if (value === "" || (Number.isInteger(numberValue) && numberValue >= 0)) {
        editableBlock = {
          ...editableBlock,
          [fieldName]: value === "" ? "" : numberValue,
        };
      } else {
        AlertService.warning("You can only enter positive integers.")
      }
    } else {
      editableBlock = {
        ...editableBlock,
        [fieldName]: value,
      };
    }

    return setLpsFieldsForSave( prev => {
      const updatedActionTypes = Array.from(new Set([
          ...(prev.action_types ?? []),
          currentType,
        "lpsc_object_condition_rules",
        "lpsc_object_types_rules",
        ])
      )
      return {
        ...prev,
        lps_config_entity_KOA_rules: {
          id: lpsFieldsForSave?.lps_config_entity_KOA_rules?.id,
          lps_config_entity_KOA_points: "",
        },
        [blockName]: editableBlock,
        action_types: updatedActionTypes,
      }
    })

    // return setLpsFieldsForSave({
    //   ...lpsFieldsForSave,
    //   // @ts-ignore
    //   objectRulesForDelete: [...lpsFieldsForSave.lpsc_object_condition_rules],
    //   // @ts-ignore
    //   objectTypeRulesForDelete: [...lpsFieldsForSave.lpsc_object_types_rules],
    //   [blockName]: editableBlock,
    //   // @ts-ignore
    //   action_types: lpsFieldsForSave.action_types.includes(currentType)
    //     ? [
    //         ...lpsFieldsForSave.action_types,
    //         "lpsc_object_condition_rules",
    //         "lpsc_object_types_rules",
    //       ]
    //     : [
    //         currentType,
    //         "lpsc_object_condition_rules",
    //         "lpsc_object_types_rules",
    //         ...lpsFieldsForSave.action_types,
    //       ],
    // });
  };

  const handleCancel = () => {
    setClearForm(true);
    setFilledFields({});
    setError("");
    setVariables([]);
    setConfigCities([]);
    setLpsFieldsForSave({
      id: null,
      user_id: null,
      lps_config_cities: [],
      general_fields: {
        name: "",
        calculation_type: lpsFormTypes.NON_INDUSTRIAL,
        config_type: "",
        object_type: "",
        entity_type: "",
        // city: "", //temp till search impl.
        // city:[],
      },
      lpsc_object_condition_rules: [
        {
          variable: "units amount",
          condition: "",
          condition_value: "",
          condition_points: "",
        },
      ],
      lpsc_object_types_rules: [
        {
          variable: "",
          condition: "",
          unit_size: "",
        },
      ],
      lps_config_entity_KOA_rules: {
        lps_config_entity_KOA_points: "",
      },
      action_types: [],
      cityForDelete: [],
      validationMode: false,
      objectRulesForDelete: [],
      objectTypeRulesForDelete: [],
    });
  };

  const handleGetAllConfigs = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    dispatch(receiveLPSConfigsRequest());
  };

  const handleAdd = (type: string, blockName: string) => {
    const addableType = lpsFieldsForSave[type] || {};
    let addableTypeBlock: unknown;
    if (blockName === "lpsc_object_condition_rules") {
      addableTypeBlock = addableType[blockName] || {};
      return setLpsFieldsForSave({
        ...lpsFieldsForSave,
        [blockName]: [...addableType, addableTypeBlock],
      });
    }
    if (blockName === "lpsc_object_types_rules") {
      addableTypeBlock = addableType[blockName] || {};
      return setLpsFieldsForSave({
        ...lpsFieldsForSave,
        [blockName]: [...addableType, addableTypeBlock],
      });
    }
  };

  const handleDelete = (type: string, blockName: string, index: number) => {
    const addableType = lpsFieldsForSave[type] || {};
    const addableTypeBlock = addableType[blockName] || [];
    let deletedItem: any;
    const currentType = type;
    if (blockName === "lpsc_object_condition_rules") {
      deletedItem = addableType.splice(index, 1);
      if (Object.keys(deletedItem[0]).length > 0 && deletedItem[0].id) {

        return setLpsFieldsForSave({
          ...lpsFieldsForSave,
          [type]: [...addableType],
          // @ts-ignore
          objectRulesForDelete: [
            // @ts-ignore
            ...lpsFieldsForSave.objectRulesForDelete,
            // @ts-ignore
            deletedItem[0],
          ],
          // @ts-ignore
          action_types: lpsFieldsForSave.action_types.includes(currentType)
            ? lpsFieldsForSave.action_types
            : [currentType, ...lpsFieldsForSave.action_types],
        });
      } else {
        return setLpsFieldsForSave({
          ...lpsFieldsForSave,
          [type]: [...addableType],
        });
      }

    }
    if (blockName === "lpsc_object_types_rules") {
      deletedItem = addableType.splice(index, 1);
      if (Object.keys(deletedItem[0]).length > 0 && deletedItem[0].id) {
        return setLpsFieldsForSave({
          [type]: [...addableType],
          // @ts-ignore
          objectRulesForDelete: [
            // @ts-ignore
            ...lpsFieldsForSave.objectTypeRulesForDelete,
            // @ts-ignore
            deletedItem[0],
          ],
          // @ts-ignore
          action_types: lpsFieldsForSave.action_types.includes(currentType)
            ? lpsFieldsForSave.action_types
            : [currentType, ...lpsFieldsForSave.action_types],
        });
      } else {
        return setLpsFieldsForSave({
          ...lpsFieldsForSave,
          [type]: [...addableType],
        });
      }
    }
    setLpsFieldsForSave({
      ...lpsFieldsForSave,
      [type]: { ...addableType, [blockName]: [...addableTypeBlock] },
    });
  };
  const cloneLpsConfig = () => {
    setLpsFieldsForSave({
      id: null,
      user_id: userId,
      lps_config_cities: [...lpsFieldsForSave.lps_config_cities],
      general_fields: {
        name: "",
        calculation_type: lpsFieldsForSave.general_fields.calculation_type,
        config_type: lpsFieldsForSave.general_fields.config_type,
        object_type: lpsFieldsForSave.general_fields.object_type,
        entity_type: lpsFieldsForSave.general_fields.entity_type,
        // city: "", //temp till search impl.
        // city:[],
      },
      lpsc_object_condition_rules: lpsFieldsForSave.lpsc_object_condition_rules,
      lpsc_object_types_rules: lpsFieldsForSave.lpsc_object_types_rules,
      lps_config_entity_KOA_rules: lpsFieldsForSave.lps_config_entity_KOA_rules,
      action_types: lpsFieldsForSave.action_types,
      cityForDelete: [],
      validationMode: false,
      objectRulesForDelete: [],
      objectTypeRulesForDelete: [],
      // base_points: 0,
      // config_type: "",
      // lps_config_entity_rules: {},
      // lpsc_entity_objects_rules: [],
      // lpsc_entity_age_rules: [],
      // lpsc_object_label_rules: [],
      // labelForDelete: [],
      // entityAgeForDelete: [],
      // entityObjForDelete: [],
    });
  };

  const saveLPSConfig = () => {
    const isValidated = validateLPSConfig(lpsFieldsForSave, lpsConfigs);
    const { result, isFilledFieldVariables } = isValidated;
    setFilledFields({ ...result });
    setVariables(isFilledFieldVariables);
    if (error) return;
    if (lpsFieldsForSave.id && !result.isFilledFieldCalculationType) {
      return AlertService.warning("The Calculation Type field is missing in your LPS configuration. Delete the configuration and recreate it.")
    }
    const isValid = Object.keys(result).every((item) => result[item] === true);
    if (!isValid) return;

    const payload = {
      ...lpsFieldsForSave,
      user_id: userId,
      validationMode: true,
    };
    if (userRole !== userTypes.USER) {
      dispatch(createEditLPSConfigsRequest(payload));
    } else {
      setLpsFieldsForSave(payload);
    }
    // if (!error) {
    //   if (!Object.keys(result).every((item) => result[item] === true)) {
    //     return setLpsFieldsForSave({
    //       ...lpsFieldsForSave,
    //       user_id: userId,
    //       validationMode: true,
    //     });
    //   }
    //   if (userRole !== userTypes.USER) {
    //     /** probably later we need to add props.user_id here to check who created this config**/
    //     result && !error &&
    //     dispatch(
    //       createEditLPSConfigsRequest({
    //         ...lpsFieldsForSave,
    //       })
    //     );
    //   }
    handleCancel();
    setFilledFields({});
    setVariables([]);
  };

  const removeLPSConfig = () => {
    if (userRole !== userTypes.USER) {
      dispatch(deleteLPSConfigRequest(lpsFieldsForSave.id));
    }
    handleCancel();
    setFilledFields({});
    setVariables([]);
  };
  return (
    <>
      <Box>
        <AppBar position="relative" sx={styles.dataBox}>
          <Toolbar sx={styles.toolBar}>
            <Typography component="div" sx={styles.headerContent}>
              Lead priority system (LPS)
            </Typography>
            <Button
              color="inherit"
              sx={styles.buttonContent}
              onClick={handleGetAllConfigs}
            >
              SELECT LPS CONFIG
            </Button>
            <Popover
              id={id}
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
            >
              <MenuList>
                {lpsConfigs &&
                  lpsConfigs.length > 0 &&
                  lpsConfigs.map((config: any) => (
                    <MenuItem
                      key={config.id}
                      onClick={() => selectConfig(config)}
                    >
                      {config.name}
                    </MenuItem>
                  ))}
              </MenuList>
            </Popover>
          </Toolbar>
        </AppBar>
      </Box>
      <Paper sx={styles.formHolder}>
        <Box sx={styles.menuPanel}>
          <Tooltip title={"Delete config"}>
            <span
              style={{
                display: 'inline-block',
              }}
            >
              <IconButton
                onClick={removeLPSConfig}
                disabled={!lpsFieldsForSave.id}
              >
              <DeleteIcon sx={{ color: "#495057" }} />
            </IconButton>
            </span>

          </Tooltip>
          <Tooltip title={"Clear form"}>
            <IconButton onClick={handleCancel}>
              <Clear sx={{ color: "#495057" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={"Clone LPS config by current"}>
            <IconButton onClick={cloneLpsConfig}>
              <ContentCopy sx={{ color: "#495057" }} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              'Clue: note, that "units amount" variable in "object point condition rules" is required'
            }
          >
            <IconButton>
              <Info sx={{ color: "#495057" }} />
            </IconButton>
          </Tooltip>
        </Box>
        {lpsFieldsForSave.general_fields.calculation_type ===
        lpsFormTypes.KADASTER ? (
          <Box>
            <LPSGeneralFieldsBlock
              calculationType={lpsFieldsForSave.general_fields.calculation_type}
              idLPS={lpsFieldsForSave?.id}
              actionChangeCity={handleChangeCity}
              onChange={handleChange}
              resetForm={clearForm}
              generalFields={lpsFieldsForSave.general_fields}
              lpsConfigCities={lpsFieldsForSave.lps_config_cities}
              configLpsCities={configCities}
              isFilledFields={filledFields}
              changeFilledFields={setFilledFields}
              error={error}
              handlerBlurName={handlerBlurName}
            />
            <LPSEntityKOABlock
              onChange={handleChangeEntityKOA}
              entityKOAValue={
                lpsFieldsForSave.lps_config_entity_KOA_rules
                  .lps_config_entity_KOA_points
              }
              isFilledFields={filledFields}
              changeFilledFields={setFilledFields}
            />
          </Box>
        ) : (
          <Box>
            <LPSGeneralFieldsBlock
              calculationType={lpsFieldsForSave.general_fields.calculation_type}
              idLPS={lpsFieldsForSave?.id}
              actionChangeCity={handleChangeCity}
              onChange={handleChange}
              resetForm={clearForm}
              generalFields={lpsFieldsForSave.general_fields}
              lpsConfigCities={lpsFieldsForSave.lps_config_cities}
              configLpsCities={configCities}
              isFilledFields={filledFields}
              changeFilledFields={setFilledFields}
              error={error}
              handlerBlurName={handlerBlurName}
            />
            <ObjectPointsBlock
              onChange={handleChangeObjectConditionRules}
              objectPointsRules={lpsFieldsForSave.lpsc_object_condition_rules}
              calculationType={lpsFieldsForSave.general_fields.calculation_type}
              onAdd={handleAdd}
              onDelete={handleDelete}
              isFilledFields={filledFields}
              changeFilledFields={setFilledFields}
              isVariables = {variables}
              changeIsVariables ={setVariables}
            />
            <ObjectTypesBlock
              onChange={handleChangeObjectTypesRules}
              objectTypesRules={lpsFieldsForSave.lpsc_object_types_rules}
              onAdd={handleAdd}
              onDelete={handleDelete}
            />
          </Box>
        )}
        <Box sx={styles.buttonHolder}>
          <Button
            variant="contained"
            sx={styles.buttonSave}
            onClick={saveLPSConfig}
          >
            Save
          </Button>
          <Button
            variant="contained"
            sx={styles.buttonCansel}
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </Box>
      </Paper>
    </>
  );
};

function mapStateToProps(state: RootState) {
  const lpsForm = selectLPS(state);
  const user = selectUser(state);
  return { user, lpsForm };
}

export default connect(mapStateToProps)(LPSForm);
